import { shuffle } from '../utils'

export type Difficulty = 'easy' | 'medium' | 'hard'
export type Operator = '+' | '-' | '×' | '÷'

export interface NumberCard {
  id:           number
  value:        number
  inExpression: boolean
}

export interface NumberToken   { type: 'number';      cardId: number; value: number }
export interface OperatorToken { type: 'operator';    op: Operator }
export interface ParenOpenToken  { type: 'paren-open'  }
export interface ParenCloseToken { type: 'paren-close' }
export type ExprToken = NumberToken | OperatorToken | ParenOpenToken | ParenCloseToken

interface DiffConfig {
  cardCount:  number
  minVal:     number
  maxVal:     number
  ops:        Operator[]
  maxGoal:    number
  roundCount: number
  allowParens: boolean
}

export const DIFFICULTY_CONFIG: Record<Difficulty, DiffConfig> = {
  easy:   { cardCount: 4, minVal: 1, maxVal:  9, ops: ['+', '-'],            maxGoal:  30, roundCount: 5, allowParens: false },
  medium: { cardCount: 6, minVal: 1, maxVal:  9, ops: ['+', '-', '×'],       maxGoal:  80, roundCount: 7, allowParens: true  },
  hard:   { cardCount: 7, minVal: 1, maxVal: 12, ops: ['+', '-', '×', '÷'],  maxGoal: 120, roundCount: 8, allowParens: true  },
}

function applyOp(a: number, op: Operator, b: number): number | null {
  if (op === '+') return a + b
  if (op === '-') return a - b
  if (op === '×') return a * b
  if (op === '÷') {
    if (b === 0 || a % b !== 0) return null
    return a / b
  }
  return null
}

function evalFlat(tokens: ExprToken[]): number | null {
  const numbers: number[] = []
  const ops: Operator[]   = []
  for (const t of tokens) {
    if (t.type === 'number')   numbers.push(t.value)
    else if (t.type === 'operator') ops.push(t.op)
  }
  if (numbers.length === 0) return null
  let result = numbers[0]
  for (let i = 1; i < numbers.length; i++) {
    const r = applyOp(result, ops[i - 1], numbers[i])
    if (r === null) return null
    result = r
  }
  return result
}

/** Evaluate expression with arbitrary-depth parentheses (left-to-right inside each group). */
export function evalExpression(tokens: ExprToken[]): number | null {
  let current = [...tokens]

  // Resolve innermost paren pairs iteratively until none remain
  while (current.some((t) => t.type === 'paren-open')) {
    // Find first close-paren, then the last open-paren before it
    const closeIdx = current.findIndex((t) => t.type === 'paren-close')
    if (closeIdx === -1) return null

    let openIdx = -1
    for (let i = closeIdx - 1; i >= 0; i--) {
      if (current[i].type === 'paren-open') { openIdx = i; break }
    }
    if (openIdx === -1) return null

    const inner  = current.slice(openIdx + 1, closeIdx)
    const result = evalFlat(inner)
    if (result === null) return null

    current = [
      ...current.slice(0, openIdx),
      { type: 'number', cardId: -1, value: result } as ExprToken,
      ...current.slice(closeIdx + 1),
    ]
  }

  return evalFlat(current)
}

/** Count unclosed open-parens in an expression. */
export function countOpenParens(tokens: ExprToken[]): number {
  let n = 0
  for (const t of tokens) {
    if (t.type === 'paren-open')  n++
    if (t.type === 'paren-close') n--
  }
  return Math.max(0, n)
}

const GEN_OPS: Operator[] = ['+', '-', '×']

export function generateAllPuzzles(difficulty: Difficulty): { cards: NumberCard[]; goal: number }[] {
  const { roundCount } = DIFFICULTY_CONFIG[difficulty]
  return Array.from({ length: roundCount }, () => generatePuzzle(difficulty))
}

export function generatePuzzle(difficulty: Difficulty): { cards: NumberCard[]; goal: number } {
  const { cardCount, minVal, maxVal, maxGoal } = DIFFICULTY_CONFIG[difficulty]

  for (let attempt = 0; attempt < 40; attempt++) {
    const values = Array.from({ length: cardCount }, () =>
      minVal + Math.floor(Math.random() * (maxVal - minVal + 1))
    )

    const subsetSize = 2 + Math.floor(Math.random() * (cardCount - 1))
    const subset     = shuffle(values).slice(0, subsetSize)

    const tokens: ExprToken[] = []
    tokens.push({ type: 'number', cardId: 0, value: subset[0] })
    for (let i = 1; i < subset.length; i++) {
      const op = GEN_OPS[Math.floor(Math.random() * GEN_OPS.length)]
      tokens.push({ type: 'operator', op })
      tokens.push({ type: 'number', cardId: i, value: subset[i] })
    }

    const result = evalExpression(tokens)
    if (result !== null && result > 0 && result <= maxGoal && Number.isInteger(result)) {
      const cards = values.map((value, i) => ({ id: i, value, inExpression: false }))
      return { cards, goal: result }
    }
  }

  // Fallback: sum of all cards
  const values = Array.from({ length: cardCount }, () =>
    minVal + Math.floor(Math.random() * (maxVal - minVal + 1))
  )
  const cards = values.map((value, i) => ({ id: i, value, inExpression: false }))
  const goal  = values.reduce((a, b) => a + b, 0)
  return { cards, goal }
}
